/**
 * Meta-description quality constants and corpus-uniqueness utilities.
 *
 * Background (June 2026 EEAT audit): all 368 published reports shared one
 * verbatim meta-description template, which is the textbook scaled-content
 * fingerprint. The v3 generator writes bespoke descriptions instead; these
 * helpers enforce the two hard guarantees:
 *   1. No banned canned frames (the old template and testing claims).
 *   2. No shared 8-gram between any two live reports' descriptions.
 */

/** Bump when the meta prompt/validation contract changes. Nodes written with
 *  an older version are flagged as upgrade targets by the publication
 *  readiness contract in status.ts. */
export const SEO_META_PROMPT_VERSION = 'meta-v3-bespoke'

export const META_DESCRIPTION_MIN_CHARS = 90
export const META_DESCRIPTION_MAX_CHARS = 165
/** Validation accepts slightly past the advertised max: the prompt asks for
 *  165 so the model aims tight, but rejecting a 166-char description wastes
 *  retries on a difference Google's pixel-based truncation cannot see. */
export const META_DESCRIPTION_MAX_GRACE = 5
export const META_DESCRIPTION_TARGET = '120-160'

/** Number of consecutive normalized tokens that constitute "shared phrasing"
 *  between two descriptions. 8 is long enough that product names and common
 *  category vocabulary never collide, short enough that a reused sentence
 *  skeleton always does. */
export const META_SHINGLE_SIZE = 8

/** Phrases that must never appear in a meta description: the retired
 *  template's fingerprints, ranking-event narration, testing claims we
 *  cannot back, and competitor references. Checked case-insensitively
 *  against normalized text. */
export const META_DESCRIPTION_BANNED_PHRASES = [
  'what the internet actually recommends',
  'with data.',
  'came out on top for one core reason',
  'we tested',
  'hands-on',
  'hands on testing',
  'wirecutter',
  'consumer reports',
] as const

/** Lowercase, strip punctuation, and map digit runs to '#' so "24 products"
 *  and "212 products" produce the same shingle (the skeleton is what we are
 *  deduplicating, not the numbers). */
export function normalizeForShingles(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/\d+/g, '#')
    .replace(/[^a-z#\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
}

/** All n-token shingles of a description, as joined strings. */
export function buildMetaShingles(
  text: string,
  size: number = META_SHINGLE_SIZE
): string[] {
  const tokens = normalizeForShingles(text)
  if (tokens.length < size) {
    // Short descriptions still participate: treat the whole token run as one
    // shingle so two identical short descriptions collide.
    return tokens.length > 0 ? [tokens.join(' ')] : []
  }
  const shingles: string[] = []
  for (let i = 0; i + size <= tokens.length; i += 1) {
    shingles.push(tokens.slice(i, i + size).join(' '))
  }
  return shingles
}

/** First banned phrase found in the text, or null. */
export function findBannedMetaPhrase(text: string): string | null {
  const haystack = text.toLowerCase()
  for (const phrase of META_DESCRIPTION_BANNED_PHRASES) {
    if (haystack.includes(phrase)) return phrase
  }
  return null
}

/** First shingle of `text` that collides with the corpus set, or null. */
export function findMetaShingleCollision(
  text: string,
  corpusShingles: ReadonlySet<string>
): string | null {
  for (const shingle of buildMetaShingles(text)) {
    if (corpusShingles.has(shingle)) return shingle
  }
  return null
}
